/**
 * PWA React Hooks
 * Provides React hooks for Progressive Web App functionality
 * Includes installation, notifications, offline status, and service worker management
 */

import { useState, useEffect, useCallback } from 'react';
import { pwaService } from '../services/pwaService';
import type { NotificationPermission, PushSubscriptionData } from '../services/pwaService';
import { logger } from '../utils/logger';

/**
 * Hook for PWA installation
 */
export function usePWAInstall() {
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    // Check initial states
    setCanInstall(pwaService.canInstall());
    setIsInstalled(pwaService.isInstalled());

    // Listen for install availability
    const handleInstallAvailable = () => {
      setCanInstall(true);
    };

    const handleInstallSuccess = () => {
      setIsInstalled(true);
      setCanInstall(false);
      setIsInstalling(false);
    };

    window.addEventListener('pwa-install-available', handleInstallAvailable);
    window.addEventListener('pwa-install-success', handleInstallSuccess);

    return () => {
      window.removeEventListener('pwa-install-available', handleInstallAvailable);
      window.removeEventListener('pwa-install-success', handleInstallSuccess);
    };
  }, []);

  const install = useCallback(async () => {
    if (!canInstall) return false;

    setIsInstalling(true);
    try {
      const success = await pwaService.installApp();
      if (success) {
        setIsInstalled(true);
        setCanInstall(false);
      }
      return success;
    } catch (error) {
      logger.child('PWA').error('Installation failed:', error);
      return false;
    } finally {
      setIsInstalling(false);
    }
  }, [canInstall]);

  return {
    canInstall,
    isInstalled,
    isInstalling,
    install,
  };
}

/**
 * Hook for push notifications
 */
export function usePushNotifications() {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [subscription, setSubscription] = useState<PushSubscriptionData | null>(null);
  const [isSubscribing, setIsSubscribing] = useState(false);

  useEffect(() => {
    // Check initial permission
    if ('Notification' in window) {
      setPermission(Notification.permission as NotificationPermission);
    }
  }, []);

  const requestPermission = useCallback(async () => {
    const result = await pwaService.requestNotificationPermission();
    setPermission(result);
    return result;
  }, []);

  const subscribe = useCallback(async (vapidPublicKey: string) => {
    if (permission !== 'granted') {
      const newPermission = await requestPermission();
      if (newPermission !== 'granted') {
        return null;
      }
    }

    setIsSubscribing(true);
    try {
      const subscriptionData = await pwaService.subscribeToPush(vapidPublicKey);
      setSubscription(subscriptionData);
      return subscriptionData;
    } catch (error) {
      logger.child('PWA').error('Push subscription failed:', error);
      return null;
    } finally {
      setIsSubscribing(false);
    }
  }, [permission, requestPermission]);

  const unsubscribe = useCallback(async () => {
    const success = await pwaService.unsubscribeFromPush();
    if (success) {
      setSubscription(null);
    }
    return success;
  }, []);

  const showNotification = useCallback(async (
    title: string,
    options?: NotificationOptions
  ) => {
    if (permission !== 'granted') {
      logger.child('PWA').warn('Notification permission not granted');
      return;
    }

    await pwaService.showNotification(title, options);
  }, [permission]);

  return {
    permission,
    subscription,
    isSubscribing,
    requestPermission,
    subscribe,
    unsubscribe,
    showNotification,
    isSupported: 'Notification' in window && 'PushManager' in window,
  };
}

/**
 * Hook for online/offline status
 */
export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(pwaService.isOnlineStatus());

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    pwaService.onOnline(handleOnline);
    pwaService.onOffline(handleOffline);

    return () => {
      pwaService.removeOnlineCallback(handleOnline);
      pwaService.removeOfflineCallback(handleOffline);
    };
  }, []);

  return isOnline;
}

/**
 * Hook for service worker updates
 */
export function useServiceWorkerUpdate() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const handleUpdateAvailable = () => {
      setUpdateAvailable(true);
    };

    window.addEventListener('sw-update-available', handleUpdateAvailable);

    return () => {
      window.removeEventListener('sw-update-available', handleUpdateAvailable);
    };
  }, []);

  const applyUpdate = useCallback(async () => {
    setIsUpdating(true);
    try {
      await pwaService.updateServiceWorker();
    } catch (error) {
      logger.child('PWA').error('Failed to apply update:', error);
    } finally {
      setIsUpdating(false);
    }
  }, []);

  const dismissUpdate = useCallback(() => {
    setUpdateAvailable(false);
  }, []);

  return {
    updateAvailable,
    isUpdating,
    applyUpdate,
    dismissUpdate,
  };
}

/**
 * Hook for offline actions
 */
export function useOfflineActions() {
  const isOnline = useOnlineStatus();

  const storeOfflineAction = useCallback(async (action: {
    type: string;
    data: any;
    url: string;
    method?: string;
  }) => {
    await pwaService.storeOfflineAction(action);
  }, []);

  const cacheUrls = useCallback(async (urls: string[]) => {
    await pwaService.cacheUrls(urls);
  }, []);

  return {
    isOnline,
    storeOfflineAction,
    cacheUrls,
  };
}

/**
 * Hook for PWA features detection
 */
export function usePWAFeatures() {
  const [features, setFeatures] = useState({
    serviceWorker: false,
    pushNotifications: false,
    backgroundSync: false,
    periodicSync: false,
    webShare: false,
    installPrompt: false,
  });

  useEffect(() => {
    const checkFeatures = () => {
      setFeatures({
        serviceWorker: 'serviceWorker' in navigator,
        pushNotifications: 'Notification' in window && 'PushManager' in window,
        backgroundSync: 'serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype,
        periodicSync: 'serviceWorker' in navigator && 'periodicSync' in window.ServiceWorkerRegistration.prototype,
        webShare: 'share' in navigator,
        installPrompt: 'BeforeInstallPromptEvent' in window,
      });
    };

    checkFeatures();
  }, []);

  return features;
}

/**
 * Hook for web share functionality
 */
export function useWebShare() {
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    setIsSupported('share' in navigator);
  }, []);

  const share = useCallback(async (data: {
    title?: string;
    text?: string;
    url?: string;
  }) => {
    if (!isSupported) {
      logger.child('PWA').warn('Web Share API not supported');
      return false;
    }

    try {
      await navigator.share(data);
      return true;
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        logger.child('PWA').error('Share failed:', error);
      }
      return false;
    }
  }, [isSupported]);

  return {
    isSupported,
    share,
  };
}

/**
 * Hook for PWA initialization
 */
export function usePWAInit() {
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);

  useEffect(() => {
    const initializePWA = async () => {
      try {
        await pwaService.initialize();
        setIsInitialized(true);
      } catch (error) {
        logger.child('PWA').error('PWA initialization failed:', error);
        setInitError(error instanceof Error ? error.message : 'Unknown error');
      }
    };

    initializePWA();
  }, []);

  return {
    isInitialized,
    initError,
  };
}